import {
  ILlmCreateFormulaTemplateMutation,
  ILlmCreateFormulaTemplateMutationVariables,
  LlmAbTestGroup,
  LlmChatMessageSender,
  PermissionKey,
  QueryType,
} from '../../../utils/apollo/graphqlSchema';
import { useCallback, useState } from 'react';
import { queryTypeConfigLookup } from '../../../utils/queryTypeLookup';
import { useMutation } from '@apollo/client';
import { LLM_CREATE_FORMULA_TEMPLATE } from '../../../utils/apollo/llm/mutations';
import toastMessage from '../../../utils/toastMessage';
import { assertNotNil } from '../../../utils/types';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { activeSchemaIdSelector } from '../../../redux/selectors/builder';
import { getAnalysisInfoFetchByType } from '../../../utils/hooks/useAnalysisInfoFetch/utils';
import { onEnterAdapter } from '../../../utils/inputUtils';
import { useSearchParams } from 'react-router-dom';
import useBuilderSelector from '../../../redux/selectorHooks/useBuilderSelector';
import { llmChatMessageToInput } from '../utils/validateExecutionInput';
import {
  DESCRIPTION_MAX_LENGTH,
  LLM_AB_TEST_GROUP_URL_PARAM,
} from '../../../config/constants';
import LumosIconAndWordmark from '../../LumosIconAndWordmark';
import CloseOverlayButton from '../../CloseOverlayButton';
import { SvgIcon } from '../../Icon';
import { theme } from '../../../styles/theme';
import LlmPromptLoading from './LlmPromptLoading';
import { z } from 'zod';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import {
  Card,
  Header,
  ContentRow,
  StyledTextArea,
  BuildButton,
} from './LlmPromptUI';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { buttonClicked } from '../../../utils/analytics/events/button/events';
import { ANALYTICS_PAGE_NAMES } from '../../../utils/analytics/constants';

const promptSchema = z
  .string()
  .max(
    DESCRIPTION_MAX_LENGTH,
    `Prompt cannot exceed ${DESCRIPTION_MAX_LENGTH} characters.`,
  );

interface IProps {
  queryType: QueryType;
  onClose: () => void;
}

const trackActions = {
  create: (queryType: QueryType) =>
    `lumos_build ${ANALYTICS_PAGE_NAMES[queryType]}`,
  update: (queryType: QueryType) =>
    `lumos_update  ${ANALYTICS_PAGE_NAMES[queryType]}`,
};

const LlmPrompt = ({ queryType, onClose }: IProps) => {
  const [params] = useSearchParams();
  const abTestGroup =
    params.get(LLM_AB_TEST_GROUP_URL_PARAM)?.toLowerCase() === 'test'
      ? LlmAbTestGroup.Test
      : LlmAbTestGroup.Default;

  const dispatch = useAppDispatch();
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);
  const { checkIsPermitted } = usePermission();

  const { previousMessages } = useBuilderSelector('llmContext');
  const [prompt, setPrompt] = useState('');

  const userMessages = previousMessages.filter(
    (message) => message.sender === LlmChatMessageSender.User,
  );
  const { track } = useAnalytics();

  const queryTypeDisplay =
    queryTypeConfigLookup[queryType].display.toLowerCase();

  const {
    requiredPermissionKey,
    trackContext,
    promptQueryTypeDisplay,
    buttonQueryTypeDisplay,
  } =
    queryType === QueryType.CohortSample
      ? {
          requiredPermissionKey: PermissionKey.CohortReportCreate,
          trackContext: 'cohorts',
          promptQueryTypeDisplay: 'custom cohort',
          buttonQueryTypeDisplay: 'cohort',
        }
      : {
          requiredPermissionKey: PermissionKey.AnalysisCreate,
          trackContext: 'reports',
          promptQueryTypeDisplay: `${queryTypeDisplay} report`,
          buttonQueryTypeDisplay: 'report',
        };

  const { buttonLabel, placeholder, trackAction } =
    userMessages.length > 0
      ? {
          buttonLabel: `Update prompt`,
          placeholder: `Add follow up conditions to your prompt`,
          trackAction: trackActions.update(queryType),
        }
      : {
          buttonLabel: `Build ${buttonQueryTypeDisplay}`,
          placeholder: `Use AI to generate ${promptQueryTypeDisplay}`,
          trackAction: trackActions.create(queryType),
        };

  const isPermittedToExecute = checkIsPermitted({
    requiredPermissionKeys: [requiredPermissionKey],
    schemaId: activeSchemaId,
  });

  const [llmCreateFormulaTemplate, { loading }] = useMutation<
    ILlmCreateFormulaTemplateMutation,
    ILlmCreateFormulaTemplateMutationVariables
  >(LLM_CREATE_FORMULA_TEMPLATE);

  const handleSubmitPrompt = useCallback(async () => {
    if (loading) {
      return;
    }

    const validationResult = promptSchema.safeParse(prompt);
    if (!validationResult.success) {
      toastMessage({
        message: validationResult.error.errors[0].message,
        type: 'error',
      });
      return;
    }

    track(buttonClicked({ context: trackContext, action: trackAction }));

    try {
      const fetchResult = await llmCreateFormulaTemplate({
        variables: {
          input: {
            schemaId: activeSchemaId,
            queryType,
            abTestGroup,
            prompt,
            previousMessages: previousMessages.map(llmChatMessageToInput),
          },
        },
      });
      const resp = fetchResult.data?.llmCreateFormulaTemplate;
      assertNotNil(resp);
      if (printGraphqlErrors(resp.errors)) {
        return;
      }
      assertNotNil(resp.entity);
      const { responseMessage, template } = resp.entity;
      if (template != null) {
        const onAnalysisInfoFetch = getAnalysisInfoFetchByType[queryType];
        onAnalysisInfoFetch(dispatch, template);
      } else {
        toastMessage({ message: responseMessage.text, type: 'error' });
      }
      setPrompt('');
    } catch (e) {
      console.error('Unexpected error', e);
      toastMessage({ message: 'Unexpected error', type: 'error' });
    }
  }, [
    activeSchemaId,
    dispatch,
    llmCreateFormulaTemplate,
    loading,
    previousMessages,
    prompt,
    setPrompt,
    queryType,
    abTestGroup,
    track,
    trackAction,
    trackContext,
  ]);

  // XXX for now the behavior when the user has no permission to use this is to not display it at all.
  // This may change in the future, in which case we should remove this and appropriately disable all
  // controls below
  if (!isPermittedToExecute) {
    return null;
  }
  return (
    <Card>
      <Header>
        <LumosIconAndWordmark />
        <CloseOverlayButton onClick={onClose} />
      </Header>
      <ContentRow>
        {loading ? (
          <LlmPromptLoading
            buttonQueryTypeDisplay={buttonQueryTypeDisplay}
            promptQueryTypeDisplay={promptQueryTypeDisplay}
          />
        ) : (
          <>
            <StyledTextArea
              placeholder={placeholder}
              disabled={loading}
              value={prompt}
              onChange={setPrompt}
              onKeyDown={onEnterAdapter(handleSubmitPrompt)}
              maxLength={DESCRIPTION_MAX_LENGTH}
            />
            <BuildButton
              disabled={loading || !prompt.trim()}
              onClick={handleSubmitPrompt}
              isOutlined={false}
            >
              {buttonLabel}
              <SvgIcon
                name="arrow-square-down"
                width={18}
                height={18}
                customFill={theme.colors.white}
              />
            </BuildButton>
          </>
        )}
      </ContentRow>
    </Card>
  );
};

export default LlmPrompt;
